/**
 * Combine all reducers in this file and export the combined reducers.
 */
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { A2A_TRANSFERS_REDUCER_KEY, a2aTransfersReducer } from 'app-components/flux/a2aTransfers';
import { ACCOUNT_DISCOVERY_REDUCER_KEY, accountDiscoveryReducer } from 'app-components/flux/account-discovery';
import { ACCOUNTS_REDUCER_KEY, accountsReducer } from 'app-components/flux/accounts';
import { ACCOUNTS_BALANCES_REDUCER_KEY, accountsBalancesReducer } from 'app-components/flux/accounts-balances';
import {
  ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY,
  accountsBalancesHistoryReducer,
} from 'app-components/flux/accounts-balances-history';
import {
  ACCOUNTS_VALUE_CHANGES_REDUCER_KEY,
  accountsValueChangesReducer,
} from 'app-components/flux/accounts-value-changes';
import { ACHIEVEMENTS_REDUCER_KEY, achievementsReducer } from 'app-components/flux/achievements';
import { REDUCER_KEY as ALERT_RULES_REDUCER_KEY, alertRulesReducer } from 'app-components/flux/alert-rules';
import { ALERTS_REDUCER_KEY, alertsReducer } from 'app-components/flux/alerts';
import { AUTH_REDUCER_KEY, authReducer } from 'app-components/flux/auth';
import { billPresentmentSlice } from 'app-components/flux/bill-presentment';
import { billableInvoiceItemsSlice } from 'app-components/flux/billable-invoice-items/billableInvoiceItemsSlice';
import { BUMP_REDUCER_KEY, bumpReducer } from 'app-components/flux/bump';
import { BUSINESS_REDUCER_KEY, businessReducer } from 'app-components/flux/business';
import { CATEGORIES_REDUCER_KEY, categoriesReducer } from 'app-components/flux/categories';
import { coreActions } from 'app-components/flux/core-actions';
import { CREDIT_PROFILE_REDUCER_KEY } from 'app-components/flux/credit-profile/types';
import { CREDIT_REPORT_REDUCER_KEY } from 'app-components/flux/credit-report/types';
import { CUSTOMERS_REDUCER_KEY, customersReducer } from 'app-components/flux/customers';
import { DATASETS_REDUCER_KEY, datasetsReducer } from 'app-components/flux/datasets';
import { DATE_REDUCER_KEY, dateReducer } from 'app-components/flux/date';
import { DOCUMENTS_REDUCER_KEY, documentsReducer } from 'app-components/flux/documents';
import { domainLogosSlice } from 'app-components/flux/domain-logos/domainLogosSlice';
import { ENTITLEMENTS_REDUCER_KEY, entitlementsReducer } from 'app-components/flux/entitlements';
import { FEATURE_FLAGS_REDUCER_KEY, featureFlagsReducer } from 'app-components/flux/feature-flags';
import { FILTERS_REDUCER_KEY, filtersReducer } from 'app-components/flux/filters';
import { FREE_TO_SPEND_REDUCER_KEY, freeToSpendReducer } from 'app-components/flux/free-to-spend';
import { GENERATED_REPORTS_REDUCER_KEY, generatedReportsReducer } from 'app-components/flux/generated-reports';
import { GOALS_REDUCER_KEY, goalsReducer } from 'app-components/flux/goals';
import { INSTITUTION_LOGINS_REDUCER_KEY, institutionLoginsReducer } from 'app-components/flux/institution-logins';
import { INSTITUTIONS_REDUCER_KEY, institutionsReducer } from 'app-components/flux/institutions';
import {
  INVESTMENT_HOLDINGS_V2_REDUCER_KEY,
  investmentHoldingsV2Reducer,
} from 'app-components/flux/investment-holdings-v2';
import { INVESTMENT_IRR_REDUCER_KEY, investmentIRRReducer } from 'app-components/flux/investment-irr';
import { INVESTMENT_QUOTES_V2_REDUCER_KEY, investmentQuotesV2Reducer } from 'app-components/flux/investment-quotes-v2';
import { INVESTMENT_SYMBOLS_KEY, investmentSymbolsReducer } from 'app-components/flux/investment-symbols';
import { INVESTMENT_TWRR_REDUCER_KEY, investmentTWRRReducer } from 'app-components/flux/investment-twrr';
import { INVESTMENTS_NEWSFEED_REDUCER_KEY, investmentsNewsfeedReducer } from 'app-components/flux/investments-newsfeed';
import { invoiceItemsSlice } from 'app-components/flux/invoice-items/invoiceItemsSlice';
import { llmSlice } from 'app-components/flux/llmInterface/llmSlice';
import { mileageTrackerPurposeSlice } from 'app-components/flux/mileage-tracker-purpose/mileageTrackerPurposeSlice';
import { mileageTrackerRatesSlice } from 'app-components/flux/mileage-tracker-rates/mileageTrackerRatesSlice';
import { mileageTrackerVehicleSlice } from 'app-components/flux/mileage-tracker-vehicle/mileageTrackerVehicleSlice';
import { mileageTrackerSlice } from 'app-components/flux/mileage-tracker/mileageTrackerSlice';
import { MILESTONE_REDUCER_KEY, milestonesReducer } from 'app-components/flux/milestones';
import { PAYMENT_METHODS_REDUCER_KEY, paymentMethodsReducer } from 'app-components/flux/payment-methods';
import { POSTPONED_ACTIONS_REDUCER_KEY, postponedActionsReducer } from 'app-components/flux/postponed-actions';
import { PREFERENCES_V2_REDUCER_KEY, preferencesV2Reducer } from 'app-components/flux/preferences-v2';
import { PROFILE_REDUCER_KEY, profileReducer } from 'app-components/flux/profile';
import { PROJECTS_REDUCER_KEY, projectsReducer } from 'app-components/flux/projects';
import {
  REDUCER_KEY as REPORT_CONFIGURATOR_REDUCER_KEY,
  reportConfigurationsReducer,
} from 'app-components/flux/report-configurations';
import { ROOTUI_REDUCER_KEY, rootUiReducer } from 'app-components/flux/root-ui';
import { salesTaxesSlice } from 'app-components/flux/sales-taxes/salesTaxesSlice';
import tickerLogosReducer from 'app-components/flux/ticker-logos/tickerLogosReducer';
import { TICKER_LOGOS_REDUCER_KEY } from 'app-components/flux/ticker-logos/tickerLogosTypes';
import { aiMemoriesSlice } from 'containers/SimplifiAiAssistant/AiAssistant/data/aiMemories/aiMemoriesSlice';
import { retirementPlannerSlice } from 'data/retirementPlanner/retirementPlannerSlice';

import { aiPromptsSlice } from 'app-components/flux/ai/ai-prompts/aiPromptsSlice';
import {
  SCHEDULED_TRANSACTIONS_REDUCER_KEY,
  scheduledTransactionsReducer,
} from 'app-components/flux/scheduled-transactions';
import { SECURITIES_REDUCER_KEY, securitiesReducer } from 'app-components/flux/securities';
import { SHARES_REDUCER_KEY, sharesReducer } from 'app-components/flux/shares';
import { SPENDING_WATCH_LIST_REDUCER_KEY, spendingWatchlistReducer } from 'app-components/flux/spending-watchlist';
import { logoStorageSlice } from 'app-components/flux/storage/logoStorageSlice';
import { SUBSCRIPTIONS_REDUCER_KEY, subscriptionsReducer } from 'app-components/flux/subscriptions';
import { TAGS_REDUCER_KEY, tagsReducer } from 'app-components/flux/tags';
import { TAX_FORMS_REDUCER_KEY, taxFormsReducer } from 'app-components/flux/tax-forms';
import { TAX_LINE_ITEMS_REDUCER_KEY, taxLineItemsReducer } from 'app-components/flux/tax-line-items';
import taxesReducer from 'app-components/flux/taxes/taxesReducer';
import { TAXES_REDUCER_KEY } from 'app-components/flux/taxes/taxesTypes';
import { transactionRulesSlice } from 'app-components/flux/transaction-rules/transactionRulesSlice';
import { TRANSACTIONS_REDUCER_KEY } from 'app-components/flux/transactions';
import { UNDO_REDUCER_KEY, undoReducer } from 'app-components/flux/undo';
import { USERINFO_REDUCER_KEY, userInfoReducer } from 'app-components/flux/user-info';
import { AI_CONVERSATIONS_REDUCER_KEY, aiConversationsReducer } from 'data/aiConversations/aiConversationsReducer';

import uiStateReducer, { UISTATE_REDUCER_KEY } from 'components/UIState/reducer';

import stdFormReducer, { REDUCER_KEY as STD_FORM_DATA_REDUCER_KEY } from 'data/StdForm/reducer';
import appReducer, { APP_REDUCER_KEY } from 'data/app/reducer';
import clipboardReducer, { REDUCER_KEY as CLIPBOARD_REDUCER_KEY } from 'data/clipboard/reducer';
import preferencesReducer, { REDUCER_KEY as PREFERENCES_REDUCER_KEY } from 'data/preferences/reducer';

import { combineSlices } from '@reduxjs/toolkit';
import creditProfileReducer from 'app-components/flux/credit-profile/reducer';
import creditReportReducer from 'app-components/flux/credit-report/reducer';
import { HOUSESTATS_REDUCER_KEY, houseStatsReducer } from 'app-components/flux/house-stats';
import { HOUSINGSELECTION_REDUCER_KEY, housingSelectionReducer } from 'app-components/flux/housing-selection';
import { VEHICLE_REDUCER_KEY, vehicleReducer } from 'app-components/flux/vehicle';
import fundingAccountsReducer, { FUNDING_ACCOUNTS_REDUCER_KEY } from 'data/fundingAccounts/reducer';
import notificationsReducer, { NOTIFICATIONS_REDUCER_KEY } from 'data/notifications/notificationsReducer';
import transactionsReducer from 'data/transactions/reducer';
import transferBalanceReducer, { TRANSFER_BALANCE_REDUCER_KEY } from 'data/transferBalance/transferBalanceReducer';
import transferHistoryReducer, { TRANSFER_HISTORY_REDUCER_KEY } from 'data/transferHistory/transferHistoryReducer';
import tripsReducer, { REDUCER_KEY as TRIPS_REDUCER_KEY } from 'data/trips/reducer';

import type { RootState } from 'store';

/*
 * routeReducer
 *
 * The reducer merges route location changes into our immutable state.
 * The change is necessitated by moving to react-router-redux@5
 *
 */

// Combine Global App Reducers. The injectedReducers argument is required to
// suuport hot-loading for dynamically loaded reducers.
//
const appReducers = combineSlices(
  //  ADD THINGS IN ALPHABETICAL ORDER PLEASE
  aiMemoriesSlice,
  aiPromptsSlice,
  billableInvoiceItemsSlice,
  billPresentmentSlice,
  llmSlice,
  domainLogosSlice,
  invoiceItemsSlice,
  logoStorageSlice,
  mileageTrackerSlice,
  mileageTrackerPurposeSlice,
  mileageTrackerRatesSlice,
  mileageTrackerVehicleSlice,
  salesTaxesSlice,
  retirementPlannerSlice,
  transactionRulesSlice,
  {
    //  ADD THINGS IN ALPHABETICAL ORDER PLEASE
    [A2A_TRANSFERS_REDUCER_KEY]: a2aTransfersReducer,
    [ACCOUNT_DISCOVERY_REDUCER_KEY]: accountDiscoveryReducer,
    [ACCOUNTS_BALANCES_REDUCER_KEY]: accountsBalancesReducer,
    [ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY]: accountsBalancesHistoryReducer,
    [ACCOUNTS_VALUE_CHANGES_REDUCER_KEY]: accountsValueChangesReducer,
    [ACCOUNTS_REDUCER_KEY]: accountsReducer,
    [AI_CONVERSATIONS_REDUCER_KEY]: aiConversationsReducer,
    [ACHIEVEMENTS_REDUCER_KEY]: achievementsReducer,
    [ALERT_RULES_REDUCER_KEY]: alertRulesReducer,
    [ALERTS_REDUCER_KEY]: alertsReducer,
    [APP_REDUCER_KEY]: appReducer,
    [AUTH_REDUCER_KEY]: authReducer,
    [BUMP_REDUCER_KEY]: bumpReducer,
    [BUSINESS_REDUCER_KEY]: businessReducer,
    [CATEGORIES_REDUCER_KEY]: categoriesReducer,
    [CUSTOMERS_REDUCER_KEY]: customersReducer,
    [CLIPBOARD_REDUCER_KEY]: clipboardReducer,
    [CREDIT_PROFILE_REDUCER_KEY]: creditProfileReducer,
    [CREDIT_REPORT_REDUCER_KEY]: creditReportReducer,
    [DATASETS_REDUCER_KEY]: datasetsReducer,
    [DATE_REDUCER_KEY]: dateReducer,
    [DOCUMENTS_REDUCER_KEY]: documentsReducer,
    [ENTITLEMENTS_REDUCER_KEY]: entitlementsReducer,
    [FEATURE_FLAGS_REDUCER_KEY]: featureFlagsReducer,
    [FILTERS_REDUCER_KEY]: filtersReducer,
    [FREE_TO_SPEND_REDUCER_KEY]: freeToSpendReducer,
    [FUNDING_ACCOUNTS_REDUCER_KEY]: fundingAccountsReducer,
    [GENERATED_REPORTS_REDUCER_KEY]: generatedReportsReducer,
    [GOALS_REDUCER_KEY]: goalsReducer,
    [INSTITUTION_LOGINS_REDUCER_KEY]: institutionLoginsReducer,
    [INSTITUTIONS_REDUCER_KEY]: institutionsReducer,
    [INVESTMENT_HOLDINGS_V2_REDUCER_KEY]: investmentHoldingsV2Reducer,
    [INVESTMENT_IRR_REDUCER_KEY]: investmentIRRReducer,
    [INVESTMENT_TWRR_REDUCER_KEY]: investmentTWRRReducer,
    [INVESTMENT_QUOTES_V2_REDUCER_KEY]: investmentQuotesV2Reducer,
    [INVESTMENT_SYMBOLS_KEY]: investmentSymbolsReducer,
    [INVESTMENTS_NEWSFEED_REDUCER_KEY]: investmentsNewsfeedReducer,
    [MILESTONE_REDUCER_KEY]: milestonesReducer,
    [NOTIFICATIONS_REDUCER_KEY]: notificationsReducer,
    [PAYMENT_METHODS_REDUCER_KEY]: paymentMethodsReducer,
    [POSTPONED_ACTIONS_REDUCER_KEY]: postponedActionsReducer,
    [PREFERENCES_REDUCER_KEY]: preferencesReducer,
    [PREFERENCES_V2_REDUCER_KEY]: preferencesV2Reducer,
    [PROFILE_REDUCER_KEY]: profileReducer,
    [PROJECTS_REDUCER_KEY]: projectsReducer,
    [REPORT_CONFIGURATOR_REDUCER_KEY]: reportConfigurationsReducer,
    [HOUSINGSELECTION_REDUCER_KEY]: housingSelectionReducer,
    [HOUSESTATS_REDUCER_KEY]: houseStatsReducer,
    [ROOTUI_REDUCER_KEY]: rootUiReducer,
    [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: scheduledTransactionsReducer,
    [SECURITIES_REDUCER_KEY]: securitiesReducer,
    [SHARES_REDUCER_KEY]: sharesReducer,
    [SPENDING_WATCH_LIST_REDUCER_KEY]: spendingWatchlistReducer,
    [STD_FORM_DATA_REDUCER_KEY]: stdFormReducer,
    [SUBSCRIPTIONS_REDUCER_KEY]: subscriptionsReducer,
    [TAGS_REDUCER_KEY]: tagsReducer,
    [TAX_FORMS_REDUCER_KEY]: taxFormsReducer,
    [TAX_LINE_ITEMS_REDUCER_KEY]: taxLineItemsReducer,
    [TAXES_REDUCER_KEY]: taxesReducer,
    [TICKER_LOGOS_REDUCER_KEY]: tickerLogosReducer,
    [TRANSFER_HISTORY_REDUCER_KEY]: transferHistoryReducer,
    [TRANSFER_BALANCE_REDUCER_KEY]: transferBalanceReducer,
    [TRANSACTIONS_REDUCER_KEY]: transactionsReducer,
    [TRIPS_REDUCER_KEY]: tripsReducer,
    [UISTATE_REDUCER_KEY]: uiStateReducer,
    [UNDO_REDUCER_KEY]: undoReducer,
    [USERINFO_REDUCER_KEY]: userInfoReducer,
    [VEHICLE_REDUCER_KEY]: vehicleReducer,

    // TODO: MAKE SURE THERE ARE NO DUPLICATE ENTRIES
    //  ADD THINGS IN ALPHABETICAL ORDER PLEASE
  },
);

// Create a Root Reducer which wraps and delegates to the App Reducers. The
// root reducer is needed to handle resetting of the redux store when a user
// logs out.
//

type WebReducersState = ReturnType<typeof appReducers>;
declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState extends WebReducersState {}
}

function isSetRoot(action: UnknownAction): action is PayloadAction<RootState> {
  return 'type' in action && action.type === coreActions.setRoot.toString();
}

const reducer = (...[state, action]: Parameters<typeof appReducers>): ReturnType<typeof appReducers> => {
  if (isSetRoot(action)) {
    return action.payload;
  }
  return appReducers(state, action);
};

export default reducer;
